import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const AI_AUTOMATION_URL = process.env.REACT_APP_AI_AUTOMATION_URL;

const resolvePublicAiApiBase = () => {
  const isBrowser = typeof window !== 'undefined' && typeof window.location !== 'undefined';
  const origin = isBrowser ? window.location.origin : 'http://localhost:3000';
  const configured = AI_AUTOMATION_URL || 'http://localhost:8100/api/v1';

  try {
    const parsed = new URL(configured, origin);
    parsed.pathname = parsed.pathname
      .replace(/\/+$/, '')
      .replace(/\/aibot\/v1$/i, '/api/v1');
    if (!/\/api\/v1$/i.test(parsed.pathname)) {
      parsed.pathname = `${parsed.pathname}/api/v1`.replace(/\/{2,}/g, '/');
    }
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return 'http://localhost:8100/api/v1';
  }
};

const PUBLIC_AI_API_BASE = resolvePublicAiApiBase();

// ── Shared page shell ────────────────────────────────────────────────────
// Defined OUTSIDE the component so React never sees a new function reference
// on re-render, which would unmount/remount children and lose input focus.
const PageShell = ({ children }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 480;
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg,#f0f4ff 0%,#e8f0fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '16px 10px' : '24px 16px',
        fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: isMobile ? '12px' : '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
          padding: isMobile ? '24px 18px' : '40px 36px',
          maxWidth: '520px',
          width: '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const RescheduleFormPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [interviewInfo, setInterviewInfo] = useState(null);

  const [formData, setFormData] = useState({
    mobile_number: '',
    reason: '',
  });
  const [mobileError, setMobileError] = useState('');

  // Multiple preferred dates
  const [preferredDates, setPreferredDates] = useState(['']);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${PUBLIC_AI_API_BASE}/interviews/reschedule-form/${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.detail || 'This link is invalid or has expired.');
          return;
        }
        const data = await res.json();
        setInterviewInfo(data);
      } catch {
        setError('Unable to load form. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const addDate = () => {
    if (preferredDates.length < 5) {
      setPreferredDates((prev) => [...prev, '']);
    }
  };

  const removeDate = (idx) => {
    setPreferredDates((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateDate = (idx, val) => {
    setPreferredDates((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validDates = preferredDates.filter((d) => d);
    if (!formData.mobile_number.trim() || validDates.length === 0 || !formData.reason.trim()) {
      setError('Please fill in all required fields and select at least one preferred date.');
      return;
    }
    // Validate mobile number: exactly 10 digits, first digit > 5
    const mobileDigits = formData.mobile_number.replace(/\D/g, '');
    if (mobileDigits.length !== 10) {
      setMobileError('Mobile number must be exactly 10 digits.');
      setError('Please fix the mobile number before submitting.');
      return;
    }
    if (parseInt(mobileDigits[0], 10) <= 5) {
      setMobileError('Mobile number must start with a digit greater than 5 (6–9).');
      setError('Please fix the mobile number before submitting.');
      return;
    }
    setMobileError('');
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${PUBLIC_AI_API_BASE}/interviews/reschedule-form/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile_number: formData.mobile_number,
          requested_dates: validDates,
          reason: formData.reason,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Failed to submit request. Please try again.');
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <p style={{ textAlign: 'center', color: '#6C757D', fontSize: '15px' }}>Loading…</p>
      </PageShell>
    );
  }

  if (error && !interviewInfo) {
    return (
      <PageShell>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
          <h2 style={{ margin: '0 0 8px', color: '#212529', fontSize: '20px' }}>Link Error</h2>
          <p style={{ color: '#6C757D', fontSize: '14px' }}>{error}</p>
        </div>
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg,#4CAF50,#388E3C)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{ margin: '0 0 8px', color: '#1B5E20', fontSize: '22px' }}>
            Request Submitted!
          </h2>
          <p style={{ color: '#495057', fontSize: '14px', lineHeight: '1.6' }}>
            Your reschedule request has been received. Our team will review it and get back to you
            shortly.
          </p>
          <p style={{ color: '#6C757D', fontSize: '13px', marginTop: '16px' }}>
            You may close this window.
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div
          style={{
            width: '56px',
            height: '56px',
            background: 'linear-gradient(135deg,#F57C00,#E65100)',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <h1 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: '#212529' }}>
          Request Reschedule
        </h1>
        {interviewInfo?.interview_title && (
          <p style={{ margin: 0, fontSize: '14px', color: '#6C757D' }}>
            {interviewInfo.interview_title}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} noValidate>
        {/* Name (readonly) */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Full Name</label>
          <input
            type="text"
            value={interviewInfo?.candidate_name || ''}
            readOnly
            style={{ ...inputStyle, background: '#F8F9FA', color: '#6C757D', cursor: 'default' }}
          />
        </div>

        {/* Email (readonly) */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Email Address</label>
          <input
            type="email"
            value={interviewInfo?.candidate_email || ''}
            readOnly
            style={{ ...inputStyle, background: '#F8F9FA', color: '#6C757D', cursor: 'default' }}
          />
        </div>

        {/* Mobile */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>
            Mobile Number <span style={{ color: '#E53935' }}>*</span>
          </label>
          <input
            type="tel"
            name="mobile_number"
            value={formData.mobile_number}
            onChange={(e) => {
              // Allow only digits
              const digits = e.target.value.replace(/\D/g, '');
              // Enforce max 10 digits
              if (digits.length > 10) return;
              // Validate first digit must be > 5 (i.e. 6–9)
              if (digits.length === 1 && parseInt(digits[0], 10) <= 5) {
                setMobileError('Mobile number must start with a digit greater than 5 (6–9).');
                return;
              }
              if (digits.length > 0 && parseInt(digits[0], 10) > 5) {
                setMobileError('');
              } else if (digits.length === 0) {
                setMobileError('');
              }
              setFormData((prev) => ({ ...prev, mobile_number: digits }));
            }}
            placeholder="e.g. 9876543210"
            required
            maxLength={10}
            style={{
              ...inputStyle,
              borderColor: mobileError ? '#E53935' : '#DEE2E6',
            }}
          />
          {mobileError && (
            <p style={{ color: '#E53935', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
              {mobileError}
            </p>
          )}
          <p style={{ color: '#ADB5BD', fontSize: '11px', marginTop: '4px', marginBottom: 0 }}>
            10-digit mobile number starting with 6, 7, 8, or 9
          </p>
        </div>

        {/* Preferred Dates — multiple */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '6px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>
              Preferred Dates <span style={{ color: '#E53935' }}>*</span>
            </label>
            <span style={{ fontSize: '11px', color: '#ADB5BD' }}>
              Add up to 5 dates
            </span>
          </div>

          {preferredDates.map((date, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <input
                type="date"
                value={date}
                onChange={(e) => updateDate(idx, e.target.value)}
                min={today}
                style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
              />
              {preferredDates.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeDate(idx)}
                  style={{
                    width: '36px',
                    height: '36px',
                    border: '1.5px solid #DEE2E6',
                    borderRadius: '8px',
                    background: '#fff',
                    color: '#ADB5BD',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '20px',
                    lineHeight: 1,
                  }}
                  title="Remove this date"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          {preferredDates.length < 5 && (
            <button
              type="button"
              onClick={addDate}
              style={{
                marginTop: '4px',
                padding: '7px 14px',
                border: '1.5px dashed #ADB5BD',
                borderRadius: '8px',
                background: 'transparent',
                color: '#6C757D',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span> Add another date
            </button>
          )}
        </div>

        {/* Reason */}
        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>
            Reason for Reschedule <span style={{ color: '#E53935' }}>*</span>
          </label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            placeholder="Please explain why you need to reschedule…"
            required
            rows={4}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: '96px',
              fontFamily: 'inherit',
              lineHeight: '1.5',
            }}
          />
        </div>

        {error && (
          <div
            style={{
              background: '#FFEBEE',
              border: '1px solid #FFCDD2',
              borderRadius: '8px',
              padding: '10px 14px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#C62828',
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '14px',
            background: submitting
              ? '#B0BEC5'
              : 'linear-gradient(135deg,#F57C00,#E65100)',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: submitting ? 'not-allowed' : 'pointer',
            boxShadow: submitting ? 'none' : '0 2px 8px rgba(245,124,0,0.35)',
            transition: 'opacity 0.2s',
          }}
        >
          {submitting ? 'Submitting…' : 'Submit Reschedule Request'}
        </button>
      </form>
    </PageShell>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#495057',
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid #DEE2E6',
  borderRadius: '8px',
  fontSize: '16px',       // 16px prevents iOS auto-zoom on focus
  color: '#212529',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

export default RescheduleFormPage;
